import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { API_BASE_URL } from '../../core/api-base-url.token';
import { QualiteComponent } from '../qualite/qualite.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

interface Site {
  name: string;
  satisfaction_percentage: number;
  participation_percentage: number;
  wait_perception: string | null;
  complaint_percentage: number;
  pending_replies?: number;
}

interface StatsResponse {
  month: string;
  sites: Site[];
}

@Component({
  selector: 'app-statistiques',
  standalone: true,
  imports: [CommonModule, FormsModule, QualiteComponent, TranslatePipe],
  templateUrl: './statistiques.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./statistiques.component.scss']
})
export class StatistiquesComponent implements OnInit {
  private http = inject(HttpClient);
  private base = inject(API_BASE_URL);
  private translate = inject(TranslateService);

  // Données du tableau (changent selon le mois sélectionné)
  sites: Site[] = [];

  loading = true;
  tableLoading = false;
  error: string | null = null;
  isTableVisible = true;
  selectedMonth = this.getCurrentMonth();
  currentDisplayedMonth = '';

  ngOnInit() {
    this.loadInitialData();
  }
  
  loadInitialData() {
    this.loading = true;
    this.loadStats();
  }

  getCurrentMonth(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  getAvailableMonths(): {label: string, value: string}[] {
    const months: string[] = this.translate.instant('pilotage.statistiques.months');
    const now = new Date();
    const result: {label: string, value: string}[] = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth();
      const value = `${year}-${String(month + 1).padStart(2, '0')}`;
      const label = `${months[month]} ${year}`;
      result.push({ label, value });
    }
    
    return result;
  }

  onMonthChange() {
    // Pas de gestion de scroll - juste recharger les données du tableau
    this.loadTableData();
  }
  
  loadTableData() {
    this.tableLoading = true;
    this.error = null;

    const params: any = {};
    if (this.selectedMonth !== this.getCurrentMonth()) {
      params.month = this.selectedMonth;
    }

    this.http.get<StatsResponse>(`${this.base}/api/v1/stats/organization`, { params }).subscribe({
      next: (data) => {
        // Mettre à jour seulement les données du tableau
        this.sites = data.sites || [];
        this.currentDisplayedMonth = data.month;
        this.tableLoading = false;
      },
      error: (err) => {
        console.error('Erreur chargement stats tableau:', err);
        this.error = this.translate.instant('pilotage.statistiques.states.loadError');
        this.tableLoading = false;
      }
    });
  }

  loadStats() {
    this.loading = true;
    this.error = null;

    // Charger les données du mois en cours pour l'initialisation
    this.http.get<StatsResponse>(`${this.base}/api/v1/stats/organization`).subscribe({
      next: (data) => {
        this.sites = data.sites || [];
        this.currentDisplayedMonth = data.month || this.getCurrentMonth();
        this.loading = false;
      },
      error: (err) => {
        console.error('Erreur chargement stats:', err);
        this.error = this.translate.instant('pilotage.statistiques.states.loadError');
        this.loading = false;
      }
    });
  }

  trackBySite(index: number, site: Site): string {
    return site.name;
  }

  getSatisfactionLevel(value: number): string {
    if (value >= 4) return 'high';
    if (value >= 3) return 'medium';
    return 'low';
  }

  getParticipationLevel(value: number): string {
    if (value >= 30) return 'high';
    if (value >= 15) return 'medium';
    return 'low';
  }

  getComplaintLevel(value: number): string {
    if (value <= 5) return 'high';
    if (value <= 15) return 'medium';
    return 'low';
  }

  getStatusLevel(site: Site): string {
    const satisfaction = site.satisfaction_percentage;
    const complaints = site.complaint_percentage;
    const participation = site.participation_percentage;
    
    if (satisfaction >= 4 && complaints <= 5 && participation >= 20) return 'optimal';
    if (satisfaction >= 3 && complaints <= 15 && participation >= 10) return 'acceptable';
    return 'attention';
  }

  getStatusText(site: Site): string {
    const level = this.getStatusLevel(site);
    if (level === 'optimal') return this.translate.instant('pilotage.statistiques.status.optimal');
    if (level === 'acceptable') return this.translate.instant('pilotage.statistiques.status.acceptable');
    return this.translate.instant('pilotage.statistiques.status.attention');
  }

  downloadData() {
    const headers = [
      this.translate.instant('pilotage.statistiques.csv.establishment'),
      this.translate.instant('pilotage.statistiques.csv.satisfaction'),
      this.translate.instant('pilotage.statistiques.csv.participation'),
      this.translate.instant('pilotage.statistiques.csv.waitPerception'),
      this.translate.instant('pilotage.statistiques.csv.complaints'),
      this.translate.instant('pilotage.statistiques.csv.status'),
    ];
    const csvData = [headers];

    this.sites.forEach(site => {
      csvData.push([
        site.name,
        site.satisfaction_percentage.toString(),
        site.participation_percentage.toString(),
        site.wait_perception || this.translate.instant('pilotage.statistiques.unknown'),
        site.complaint_percentage.toString(),
        this.getStatusText(site)
      ]);
    });
    
    const csvContent = csvData.map(row => row.join(';')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'statistiques-organisation.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  toggleTableVisibility() {
    this.isTableVisible = !this.isTableVisible;
  }

  formatNumber(value: number): string {
    return value % 1 === 0 ? value.toString() : value.toFixed(1);
  }

  Math = Math;
}